// eslint-disable-next-line no-unused-vars
import React from 'react';
import { func, bool, object, array, string } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Info, SlidersHorizontal, Link, X, Copy } from 'lucide-react';
import { SkeletonText, Toggle } from '@carbon/react';
import { uniqueId } from 'lodash';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ReactSelect from 'react-select';

import { NWButton, NWDropdown } from 'Components/shared';
import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';
import useToastNotifications from 'Hooks/useToastNotifications';

const StyledIPTable = styled.div`
  .urlFeedHeader {
    .activeToggle {
      max-width: fit-content;

      .bx--toggle__switch {
        margin: 0 0 0 50px;

        span {
          margin: 0;
          right: 60px;
        }
      }
    }
  }

  .urlFeedContainer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 16px 0;

    .urlFeed {
      font-size: 18px;
      font-weight: 600;
      word-break: break-all;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      flex: 1;
    }

    .copy {
      min-width: 20px;
      cursor: pointer;
      color: ${ props => props.theme.colors.altOrange };
    }
  }

  .react-select__control {
    box-shadow: none !important;

    &:hover {
      border-color: transparent !important;
    }
  }

  .react-select__multi-value {
    .react-select__multi-value__label > div {
      padding: 0 !important;
    }
  }

  .activeParameters {
    display: flex;
    gap: 10px;

    .parametersLabel {
      font-size: 14px;
      margin-top: 8px;
    }

    .parametersList {
      display: flex;
      gap: 5px;
      flex: 1;
      flex-wrap: wrap;

      .activeParam {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 5px;
        background-color: #F5F5F5;
        padding: 5px 10px;
        height: 28px;
        font-size: 14px;
        border-radius: 6px;

        .removeParamButton {
          background: none;
          border: none;
          cursor: pointer;
          width: 20px;
          height: 16px;
        }
      }
    }

  }

  .paramSettings {
    .warningMessage {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #E8720B;
      margin-bottom: 16px;
    }

    .cds--toggle__switch {
      background: #e2e2e2;

      &.cds--toggle__switch--checked {
        background: #0F233C;
      }
    }

    .toggleDiv {
      display: flex;
      align-items: end;
      margin-top: 10px;

      .react-tooltip p {
        font-size: 14px !important;
      }

      > svg {
        margin: 8px 0 10px 8px;
      }

      .toggleInput {
        max-width: 155px;
      }
    }

    .toggleInput {
      position: relative;
      max-width: 235px;

      .react-tooltip p {
        font-size: 14px !important;
      }

      .bx--toggle-input + .bx--toggle-input__label > .bx--toggle__switch::before {
        background-color: #E2E2E2;
      }

      .bx--toggle-input:checked + .bx--toggle-input__label > .bx--toggle__switch::before {
        background-color: #0F233C;
      }

      .bx--toggle-input__label {
        .toggleLabel {
          position: absolute;
          top: 15px;
          z-index: 1;
          left: 60px;
          height: 24px;
          width: max-content;
          z-index: 0;

          display: flex;
          align-items: center;
          gap: 5px;
        }
      
        .bx--toggle__text--off,
        .bx--toggle__text--on {
          display: none;
        }
      }
    }

    .mainSettings {
      display: flex;
      gap: 32px;

      label {
        text-transform: lowercase;
        font-size: 14px;
        margin: 4px 0;
        color: #3A3A3A;
        display: inline-block;
      }

      .multiSelect, .dropdownSelect {
        margin-top: 6px;
      }

      .dropdownSelect .styledDropdown {
        margin-bottom: 0;
      }

      .rightColumn, .leftColumn {
        flex: 1;
      }

      .rightColumn {
        .bx--list-box__wrapper {
          margin-top: 5px;

          .bx--label::after {
            content: normal;
          }
        }
      }

      .bx--label span {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }

    .columnsList {
      margin-top: 16px;

      h3 {
        font-size: 16px;
        font-weight: 600;
      }

      p {
        font-size: 14px;
        color: #C6C6C6;
      }

      .columnParams {
        display: grid;
        grid-auto-flow: column;
        grid-template-rows: repeat(11, auto);
        gap: 0 16px;
        padding: 16px 0;

        .columnItem {
          display: flex;
          align-items: end;
          gap: 8px;
          margin-top: 18px;

          > svg {
            margin-bottom: 4px;
          }

          .react-tooltip p {
            color: #3a3a3a
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    .activeParameters {
      flex-direction: column;
    }

    .paramSettings {
      .mainSettings {
        flex-direction: column;
        gap: 0;
      }

      .columnsList {
        .columnParams {
          grid-auto-flow: row;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: auto;
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .react-tooltip {
      max-width: 300px;
    }
    .paramSettings {
      .toggleDiv .toggleInput {
        max-width: none;
      }
      .toggleInput {
        max-width: none;
      }
      .columnsList {
        .columnParams {
          grid-template-columns: repeat(1, 1fr);
        }
      }
    }
  }
`;

const URLFeedGenerator = ({
  parameters,
  fileFormatOptions,
  currencyOptions,
  languageOptions,
  onParameterChange,
  onColumnChange,
  onClearAll,
  onClearParameter,
  generatedUrl,
  loading,
  generateApiKey,
  apiKey,
  handleCategoryChange,
  selectedCategories,
  categories,
  handleBrandChange,
  selectedBrands,
  brands,
}) => {
  const { t } = useTranslation();
  const toast = useToastNotifications();
  const theme = useTheme();
  const analytics = useAnalytics();
  const translatedLanguageOptions = (languageOptions || []).map(item => ({
    ...item,
    label: t(`common.languages.${ item.translationKey }`)
  }));

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);
    analytics.track('copy_feed_url');

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyUrlSuccess')
      });
    }
  };

  const activeParameters = [];
  if (parameters.format !== 'csv') activeParameters.push({ key: 'format', value: parameters.format });
  if (parameters.currency_id !== 'ron') activeParameters.push({ key: 'currency_id', value: parameters.currency_id });
  if (parameters.language !== 'ro') activeParameters.push({ key: 'language', value: parameters.language });
  if (parameters.category_id) activeParameters.push({ key: 'category_id', value: parameters.category_id });
  if (parameters.manufacturer_id) activeParameters.push({ key: 'manufacturer_id', value: parameters.manufacturer_id });
  if (parameters.with_vat) activeParameters.push({ key: 'with_vat', value: parameters.with_vat });
  if (parameters.resealed) activeParameters.push({ key: 'resealed', value: parameters.resealed });
  if (parameters.with_inactive) activeParameters.push({ key: 'with_inactive', value: parameters.with_inactive });

  const selectedColumns = Object.keys(parameters.columns).filter(col => parameters.columns[col]);
  selectedColumns.forEach(col => activeParameters.push({ key: 'column', value: col }));

  const renderActiveParam = param => {
    if (param.key === 'format') {
      return `${t('common.format')}: ${param.value}`;
    }
    if (param.key === 'column') {
      return `${t('common.columns')}: ${param.value}`;
    }
    return param.key;
  };

  const formatOptionLabel = ({ label, level }) => (
    <div style={ { paddingLeft: `${level * 20}px` } }>
      <div className="font-medium">{label}</div>
    </div>
  );

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
          ? '#dbeafe'
          : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      padding: '8px 12px',
      cursor: 'pointer',
      fontSize: '14px'
    }),
    control: provided => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    multiValue: provided => ({
      ...provided,
      backgroundColor: '#dbeafe',
      borderRadius: '6px'
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: '#1e40af',
      fontSize: '13px'
    }),
    multiValueRemove: provided => ({
      ...provided,
      color: '#1e40af',
      ':hover': {
        backgroundColor: '#3b82f6',
        color: 'white',
      },
    }),
  };

  return (
    <StyledIPTable theme={ theme }>
      <section>
        <div className="sectionHeader urlFeedHeader">
          <h4><Link size={ 20 } /> { t('profile.menu.urlFeed') }</h4>
          <Toggle
            className="activeToggle"
            id="url_feed_active_toggle"
            labelA={ t('common.inactive') }
            labelB={ t('common.active') }
            disabled={ !!apiKey }
            onToggle={ generateApiKey }
            toggled={ !!apiKey }
          />
        </div>
        <div className="sectionBody">
          <div className="urlFeedContainer">
            <span className="urlFeed">{ loading ? <SkeletonText /> : generatedUrl }</span>
            <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(generatedUrl) } />
          </div>
          <div className="activeParameters">
            { activeParameters.length > 0 && (
              <>
                <span className="parametersLabel">{ t('profile.activeParameters') }:</span>
                <div className="parametersList">
                  { activeParameters.map(param => (
                    <div
                      key={ uniqueId('activeParam') }
                      className="activeParam"
                    >
                      { renderActiveParam(param) }
                      <button
                        type="button"
                        className="removeParamButton"
                        onClick={ () => onClearParameter(param) }
                      ><X size={ 14 } /></button>
                    </div>
                  )) }
                </div>
                <NWButton
                  onClick={ onClearAll }
                  className="clearAllButton"
                  kind="danger"
                >
                  { t('profile.clearAllParameters') }
                </NWButton>
              </>
            ) }
          </div>
        </div>
      </section>
      <section className="urlFeed">
        <div className="sectionHeader">
          <h4><SlidersHorizontal size={ 20 } /> { t('profile.feedParams') }</h4>
        </div>
        <div className="sectionBody paramSettings">
          <div className="warningMessage">
            <Info size={ 20 } />
            <p>{ t('profile.urlFeedChangeInfo') }</p>
          </div>
          <div className="mainSettings">
            <div className="leftColumn">
              <div className="multiSelect">
                <label htmlFor="categories-select">{ t('profile.params.categories') }</label>
                <ReactSelect
                  isMulti
                  inputId="categories-select"
                  options={ categories }
                  value={ selectedCategories }
                  onChange={ handleCategoryChange }
                  formatOptionLabel={ formatOptionLabel }
                  styles={ customStyles }
                  placeholder={ t('profile.paramsPlaceholder.category_id') }
                  classNamePrefix="react-select"
                  isSearchable
                  closeMenuOnSelect={ false }
                  getOptionValue={ option => option.value }
                />
              </div>
              <div className="multiSelect">
                <label htmlFor="categories-select">{ t('profile.params.brands') }</label>
                <ReactSelect
                  isMulti
                  isLoading={ brands.length === 0 }
                  inputId="brands-select"
                  options={ brands }
                  value={ selectedBrands }
                  onChange={ handleBrandChange }
                  formatOptionLabel={ formatOptionLabel }
                  styles={ customStyles }
                  placeholder={ t('profile.paramsPlaceholder.manufacturer_id') }
                  classNamePrefix="react-select"
                  isSearchable
                  closeMenuOnSelect={ false }
                  getOptionValue={ option => option.value }
                />
              </div>
              <div className="dropdownSelect">
                <NWDropdown
                  id="currency-select"
                  inputId="currency-select"
                  titleText={ t('profile.params.currency') }
                  items={ currencyOptions }
                  onChange={ item => onParameterChange('currency_id', item.selectedItem.id) }
                  selectedItem={
                    (currencyOptions || []).find(item => item?.id === parameters?.currency_id) || ''
                  }
                />
              </div>
            </div>
            <div className="rightColumn">
              <div className="dropdownSelect">
                <NWDropdown
                  id="format-select"
                  inputId="format-select"
                  titleText={ t('common.format') }
                  onChange={ e => onParameterChange('format', e.selectedItem?.id) }
                  items={ fileFormatOptions || [] }
                  selectedItem={
                    (fileFormatOptions || []).find(item => item?.id === parameters?.format) || ''
                  }
                />
              </div>
              <div className="dropdownSelect">
                <NWDropdown
                  id="language-select"
                  inputId="language-select"
                  titleText={ t('profile.language') }
                  onChange={ e => onParameterChange('language', e.selectedItem?.id) }
                  items={ translatedLanguageOptions }
                  selectedItem={
                    translatedLanguageOptions.find(item => item?.id === parameters?.language) || ''
                  }
                />
              </div>
              <div className="toggleDiv">
                <Toggle
                  className="toggleInput"
                  id="with_vat_toggle"
                  labelText={ (
                    <span className="toggleLabel">
                      with_vat
                    </span>
                  ) }
                  hideLabel
                  onToggle={ value => onParameterChange('with_vat', value) }
                  toggled={ parameters.with_vat }
                />
                <Info size={ 18 } id="with_vat" />
                <ReactTooltip
                  anchorId="with_vat"
                  variant="light"
                  content={ (
                    <p>{ t('profile.params.with_vat') }</p>
                  ) }
                />
              </div>
              <div className="toggleDiv">
                <Toggle
                  className="toggleInput"
                  id="resealed_toggle"
                  labelText={ (
                    <span className="toggleLabel">
                      resealed
                    </span>
                  ) }
                  hideLabel
                  onToggle={ value => onParameterChange('resealed', value) }
                  toggled={ parameters.resealed }
                />
                <Info size={ 18 } id="resealed" />
                <ReactTooltip
                  anchorId="resealed"
                  variant="light"
                  content={ (
                    <p>{ t('profile.params.resealed') }</p>
                  ) }
                />
              </div>
              <div className="toggleDiv">
                <Toggle
                  className="toggleInput"
                  id="with_inactive_toggle"
                  labelText={ (
                    <span className="toggleLabel">
                      with_inactive
                    </span>
                  ) }
                  hideLabel
                  onToggle={ value => onParameterChange('with_inactive', value) }
                  toggled={ parameters.with_inactive }
                />
                <Info size={ 18 } id="with_inactive" />
                <ReactTooltip
                  anchorId="with_inactive"
                  variant="light"
                  content={ (
                    <p>{ t('profile.params.with_inactive') }</p>
                  ) }
                />
              </div>
            </div>
          </div>
          <div className="columnsList">
            <h3> { t('profile.feedColumnsTitle') }</h3>
            <p>{ t('profile.feedColumnsInfo') }.</p>
            <div className="columnParams">
              { Object.keys(parameters.columns).map(column => (
                <div key={ uniqueId('column_') } className="columnItem">
                  <Toggle
                    className="toggleInput"
                    id={ `${ column }_toggle` }
                    labelText={ (
                      <span className="toggleLabel">
                        { column }
                      </span>
                    ) }
                    hideLabel
                    onToggle={ value => onColumnChange(column, value) }
                    toggled={ parameters.columns[column] }
                  />
                  <Info size={ 18 } id={ column } />
                  <ReactTooltip
                    anchorId={ column }
                    variant="light"
                    content={ (
                      <p>{ t(`profile.params.${ column }`) }</p>
                    ) }
                  />
                </div>
              )) }
            </div>
          </div>
        </div>
      </section>
    </StyledIPTable>
  );
};

URLFeedGenerator.propTypes = {
  parameters: object,
  fileFormatOptions: array,
  currencyOptions: array,
  languageOptions: array,
  onParameterChange: func,
  onColumnChange: func,
  onClearAll: func,
  onClearParameter: func,
  generatedUrl: func,
  loading: bool,
  generateApiKey: func,
  apiKey: string,
  handleCategoryChange: func,
  selectedCategories: array,
  categories: array,
  handleBrandChange: func,
  selectedBrands: array,
  brands: array,
};

URLFeedGenerator.displayName = 'URLFeedGeneratorComponent';

export default URLFeedGenerator;
